import type { ReactNode } from 'react';

interface NoticePanelProps {
  /** Bold accent line at the top of the panel. */
  title: string;
  /** Body copy. A node rather than a string so callers can inline a link. */
  children: ReactNode;
  className?: string;
}

/**
 * The accent-bordered notice the canvas uses wherever a purchase carries a
 * legal condition — the licence warning under the product page's buy button and
 * the one in the cart sidebar are the same panel with different copy.
 *
 * Extracted because both places draw it at the same tint arithmetic (a 30%
 * accent hairline over a 6% accent wash) and the two had already started to
 * drift apart by a pixel when they were written separately.
 *
 * Deliberately not a generic alert: `--ok`/`--error` states elsewhere have
 * their own treatments, and folding those in here would turn one clear panel
 * into a variant matrix nothing asked for.
 */
export function NoticePanel({ title, children, className }: NoticePanelProps) {
  return (
    <div
      className={`flex flex-col gap-1.5 rounded border border-accent-600/30 bg-accent-600/[0.06] px-5 py-[18px]${
        className ? ` ${className}` : ''
      }`}
    >
      <span className="text-[13px] font-bold text-accent-700">{title}</span>
      <span className="text-[13.5px] leading-[1.55] text-foreground/70">{children}</span>
    </div>
  );
}
